import { useFormik } from "formik";
import { isValidNumber } from "libphonenumber-js";
import { FC, useCallback, useEffect, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import PhoneNumber from "components/PhoneNumber";
import TextInput from "components/TextInput";

import { UserFormValues } from "entities/companies";
import { CompaniesApi } from "services/http";

import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { resetErrorsCompany } from "store/slices/companiesSlice";

import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { validationSchema } from "./schema";

import styles from "../style.module.scss";

type Props = {
  onClose?: () => void;
  withCompanySelect?: boolean;
  type?: "add" | "edit";
  data?: UserFormValues;
  onSubmit?: (values?: unknown) => void;
};

const ModalUserContent: FC<Props> = ({ onClose, withCompanySelect, type, data, onSubmit }) => {
  const isCreate = type === "add";
  const dispatch = useAppDispatch();
  const { isUserLoading, errors: userErrors } = useAppSelector((state: RootStore) => state.companies);
  const [companyLabel, setCompanyLabel] = useState<string>("");

  const form = useFormik({
    initialValues: {
      name: data?.name || "",
      email: data?.email || "",
      phone: data?.phone || "",
      position: data?.position || "",
      company: "",
    },
    validationSchema: validationSchema(withCompanySelect || false, isCreate),
    onSubmit: (values) => {
      if (!isValidNumber(values.phone)) {
        form.setErrors({ phone: "Invalid phone number" });
        return;
      }

      onSubmit?.(values);
    },
    validateOnBlur: false,
    validateOnChange: false,
  });

  const handleCancel = () => {
    form.resetForm();
    onClose?.();
  };

  const handleCompanySearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      const hasMore = page * 50 < data?.total;
      const options = data?.items?.map((company) => ({ value: String(company?.id), label: company?.name }));

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleCompanyChange = useCallback((value: string, label: string) => {
    form.setFieldValue("company", value);
    setCompanyLabel(label);
  }, []);

  useEffect(() => {
    if (userErrors) {
      form.setErrors({
        ...(userErrors?.detail?.full_name && { name: userErrors.detail.full_name }),
        ...(userErrors?.detail?.email && { email: userErrors.detail.email }),
        ...(userErrors?.detail?.phone_number && { phone: userErrors.detail.phone_number }),
        ...(userErrors?.detail?.position && { position: userErrors.detail.position }),
        ...(userErrors?.detail?.company && { company: userErrors.detail.company }),
      });
    }

    return () => {
      dispatch(resetErrorsCompany());
    };
  }, [userErrors, dispatch]);

  return (
    <div className={styles.content}>
      <p className={styles.title}>
        {isCreate ? "Add a new user to the company" : "Update user information and permissions"}
      </p>

      <form onSubmit={form.handleSubmit} className={styles.form}>
        <TextInput
          isRequired
          name="name"
          label="Full name"
          placeholder="Full name"
          value={form.values.name}
          error={form.errors.name}
          onChange={form.handleChange}
          isDisabled={isUserLoading}
          maxLength={100}
        />

        {isCreate && (
          <TextInput
            isRequired
            name="email"
            label="Email"
            placeholder="Email"
            value={form.values.email}
            error={form.errors.email}
            onChange={form.handleChange}
            isDisabled={isUserLoading}
            maxLength={256}
          />
        )}

        <PhoneNumber
          isRequired
          value={form.values.phone}
          error={form.errors.phone}
          onChange={(phone) => form.setFieldValue("phone", phone)}
          isDisabled={isUserLoading}
        />

        <TextInput
          name="position"
          label="Position"
          placeholder="Position"
          value={form.values.position}
          error={form.errors.position}
          onChange={form.handleChange}
          isDisabled={isUserLoading}
          maxLength={100}
        />

        {withCompanySelect && isCreate && (
          <AsyncSearchableSelect
            isRequired
            label="Company"
            isDisabled={isUserLoading}
            value={form.values.company}
            error={form.errors.company}
            selectedLabel={companyLabel}
            placeholder="Select company"
            onSearch={handleCompanySearch}
            onChange={handleCompanyChange}
          />
        )}

        <div className={styles.actions}>
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isUserLoading}>
            Cancel
          </Button>

          <Button type="submit" variant="primary" disabled={isUserLoading}>
            {isCreate ? "Invite" : "Update"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ModalUserContent;
